import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ChatAPI, Thread, FeedbackData } from '../api/chat';
import { useToastStore } from "./toastStore";

interface Reference {
  reference_id: string;
  reference_type: 'table' | 'document' | 'image';
  title: string;
  file_id?: number;  // For document references
  page_number?: number;  // For document references
  data?: {
    columns?: string[];
    rows?: Record<string, any>[];  // Array of objects, not arrays
    metadata?: {
      sql?: string;
      row_count?: number;
      question?: string;
      timestamp?: number;
    };
  };
  timestamp: Date;
}
interface ChartSpec {
  $schema: string;
  width: number;
  height: number;
  padding: string | number | { left: number; right: number; top: number; bottom: number };
  data: { name: string; values: any[] };
  marks: any[];
  scales?: any[];
  axes?: any[];
  legends?: any[];
}
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  suggestedQuestions?: string[];
  type?: 'reasoning' | 'normal';
  reasoningSteps?: ReasoningStep[];  // Array of reasoning steps for this AI message
  references?: Reference[];  // Array of references (tables, documents, etc.)
  chartReference?: ChartSpec[];  // Vega spec for chart rendering
}

interface ReasoningStep {
  content: string;
  timestamp: Date;
  tool_name?: string;
  tool_args?: any;
  tool_result?: string;  // Full tool result without truncation
}

interface DocumentPreview {
  url: string | null;
  loading: boolean;
  error?: string | null;
  objectUrl?: string | null;
  requestId?: string;
}

interface ChatState {
  isOpen: boolean;
  isHistoryPanelOpen: boolean;
  messages: Message[];
  inputValue: string;
  isLoading: boolean;
  currentThreadId: string | null;
  threads: Thread[];
  negotiationId?: number;
  agentType?: string;
  supplierId?: number;
  tenderId?: number;
  // Auth / Config (🔑 added)
  apiBase?: string;
  documentPreviews: Record<string, DocumentPreview>;

  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  toggleHistoryPanel: () => void;
  setInputValue: (value: string) => void;
  handleChange: (file: File, negotiationId?: number, indexId?: string) => Promise<void>;
  sendMessage: (text: string, negotiationId?: number, agentType?: string, supplierId?: number, tenderId?: number) => void;
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  setLoading: (loading: boolean) => void;
  submitFeedback: (feedbackData: FeedbackData) => Promise<void>;
  createNewChat: () => Promise<void>;
  loadThread: (threadId: string) => Promise<void>;
  loadThreads: () => Promise<void>;
  setCurrentThreadId: (threadId: string | null) => void;
  setNegotiationId: (negotiationId?: number | string | null) => void;
  setAgentType: (agentType?: string | null) => void;
  setSupplierId: (supplierId?: number | string | null) => void;
  setTenderId: (tenderId?: number | string | null) => void;
  // Config setters (🔑 added)
  setApiBase: (base: string) => void;
  loadDocumentPreview: (reference: Reference) => Promise<void>;
  clearDocumentPreview: (referenceId: string) => void;

}

export const useChatStore = create<ChatState>()(
  devtools((set, get) => (
    {
    isOpen: false,
    isHistoryPanelOpen: false,
    messages: [],
    inputValue: '',
    isLoading: false,
    currentThreadId: null,
    threads: [],
    documentPreviews: {},

    openChat: () => set({ isOpen: true }),
    closeChat: () => set({ isOpen: false, isHistoryPanelOpen: false }),
    toggleChat: () => set((state) => ({ isOpen: !state.isOpen })),
    toggleHistoryPanel: () => set((state) => ({ isHistoryPanelOpen: !state.isHistoryPanelOpen })),
    setInputValue: (value) => set({ inputValue: value }),
    setNegotiationId: (negotiationId) => {
      const parsed = negotiationId !== undefined && negotiationId !== null ? Number(negotiationId) : undefined;
      set({ negotiationId: Number.isNaN(parsed as number) ? undefined : parsed });
    },
    setAgentType: (agentType) => set({ agentType: agentType ?? undefined }),
    setSupplierId: (supplierId) => {
      const parsed = supplierId !== undefined && supplierId !== null ? Number(supplierId) : undefined;
      set({ supplierId: Number.isNaN(parsed as number) ? undefined : parsed });
    },
    setTenderId: (tenderId) => {
      const parsed = tenderId !== undefined && tenderId !== null ? Number(tenderId) : undefined;
      set({ tenderId: Number.isNaN(parsed as number) ? undefined : parsed });
    },

    addMessage: (message) => set((state) => {
      const uniqueId = `${Date.now().toString()}-${Math.random().toString(36).slice(2, 8)}`;
      return {
        messages: [...state.messages, {
          ...message,
          id: uniqueId,
          timestamp: new Date()
        }]
      };
    }),

    setLoading: (loading) => set({ isLoading: loading }, false, 'setLoading'),

    handleChange: async (file, negotiationId, indexId = '1') => {
      const { addMessage, currentThreadId, negotiationId: storedNegotiationId } = get();
      const { error } = useToastStore.getState();

      const finalNegotiationId = negotiationId ?? storedNegotiationId;
      if (finalNegotiationId === undefined) {
        error('Error', 'File upload is only available within a negotiation context');
        return;
      }

      let threadId = currentThreadId;
      if (!threadId) {
        threadId = 'thread-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      }

      addMessage({
        text: `Uploading file: ${file.name}...`,
        sender: 'ai'
      });

      const tempMessages = get().messages;
      const tempMessageId = tempMessages[tempMessages.length - 1]?.id;

      try {
        const responseStream = await ChatAPI.uploadFile(file, {
          negotiationId: finalNegotiationId,
          indexId,
          thread_id: threadId
        });

        const reader = responseStream?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response stream received');
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (!line.trim()) continue;

            let jsonStr = line.startsWith('data: ') ? line.substring(6) : line;

            if (jsonStr.trim() === '[DONE]') {
              continue;
            }

            try {
              const data = JSON.parse(jsonStr);

              const messages = [...get().messages];
              if (tempMessageId) {
                const tempIndex = messages.findIndex(msg => msg.id === tempMessageId);
                if (tempIndex >= 0) {
                  const progressText = data.progress ? ` (${data.progress}%)` : '';
                  messages[tempIndex] = {
                    ...messages[tempIndex],
                    text: `${data.message}${progressText}`
                  };
                  set({ messages }, false, 'fileUploadProgress');
                }
              }

              if (data.status === 'completed') {
                addMessage({
                  text: `File uploaded successfully! ${data.node_count ? `Created ${data.node_count} chunks.` : ''}`,
                  sender: 'ai'
                });

                if (!get().currentThreadId && data.thread_id) {
                  set({ currentThreadId: data.thread_id }, false, 'setThreadFromUpload');
                }
              } else if (data.status === 'error') {
                addMessage({
                  text: `Error: ${data.message}`,
                  sender: 'ai'
                });
              }
            } catch (err) {
              console.error('Error parsing stream data:', err, line);
            }
          }
        }
      } catch (error) {
        console.error('File upload error:', error);
        addMessage({
          text: `File upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          sender: 'ai'
        });
        throw error;
      }
    },

    sendMessage: async (text, negotiationId, agentType, supplierId, tenderId) => {
      const {
        addMessage,
        setLoading,
        currentThreadId,
        negotiationId: storedNegotiationId,
        agentType: storedAgentType,
        supplierId: storedSupplierId,
        tenderId: storedTenderId
      } = get();

      const finalNegotiationId = negotiationId ?? storedNegotiationId;
      const finalAgentType = agentType ?? storedAgentType;
      const finalSupplierId = supplierId ?? storedSupplierId;
      const finalTenderId = tenderId ?? storedTenderId;

      // If no current thread, generate a temporary thread ID for the new thread
      let threadId = currentThreadId;
      if (!threadId) {
        // Generate a unique thread ID for the new thread
        threadId = 'thread-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      }

      addMessage({ text, sender: 'user' });
      setLoading(true);
      addMessage({ text: '', sender: 'ai' });

      try {
        const responseStream = await ChatAPI.sendMessage(text, threadId, finalNegotiationId, finalAgentType, finalSupplierId, finalTenderId);

        if (!responseStream) {
          throw new Error('No response stream received');
        }

        const reader = responseStream.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = '';
        let suggestedQuestions: string[] = [];
        let reasoningSteps: ReasoningStep[] = [];
        let references: Reference[] = [];

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonStr = line.slice(6).trim();
                  if (!jsonStr) continue;

                  const data = JSON.parse(jsonStr);

                  if (data.content === '[DONE]') {
                    setLoading(false);
                    break;
                  }

                  const state = get();
                  const messages = [...state.messages];

                  // Handle user message ID update (first chunk from backend)
                  if (data.role === 'user' && data.message_id && data.user_message) {
                    // Find and update the user message with database ID
                    for (let i = messages.length - 1; i >= 0; i--) {
                      if (messages[i].sender === 'user' && messages[i].text === data.user_message) {
                        messages[i] = {
                          ...messages[i],
                          id: data.message_id
                        };
                        set({ messages }, false, 'userMessageIdUpdate');
                        break;
                      }
                    }
                    continue; // Skip further processing for user message info
                  }
                  if (data.type === 'chart') {
                    const datas = data.content.data;
                    const hydratedSpec = {
                      ...data.content.spec,
                      data: {
                        name: datas.name || 'dataset',
                        values: datas.values,
                      },
                    };

                    // Update AI message with chart references
                    const lastMessageIndex = messages.length - 1;
                    if (lastMessageIndex >= 0 && messages[lastMessageIndex].sender === 'ai') {
                      messages[lastMessageIndex] = {
                        ...messages[lastMessageIndex],
                        chartReference: hydratedSpec
                      };
                      set({ messages }, false, 'chartUpdate');
                    }
                    continue; // Don't accumulate reference in main text
                    // // Currently, we just log the chart event. You can implement chart rendering as needed.
                    // continue; // Don't accumulate chart info in main text

                  }
                  // Handle reference events
                  if (data.type === 'reference') {

                    // Create reference object
                    const reference: Reference = {
                      reference_id: data.reference_id,
                      reference_type: data.reference_type,
                      title: data.title,
                      file_id: data.file_id,  // Add file_id for document references
                      page_number: data.page_number,  // Add page_number for document references
                      data: data.data,
                      timestamp: new Date(data.timestamp * 1000)
                    };


                    // Add reference to accumulation array
                    if (!references) {
                      references = [];
                    }
                    references.push(reference);

                    // Update AI message with references
                    const lastMessageIndex = messages.length - 1;
                    if (lastMessageIndex >= 0 && messages[lastMessageIndex].sender === 'ai') {
                      messages[lastMessageIndex] = {
                        ...messages[lastMessageIndex],
                        references: [...references]
                      };
                      set({ messages }, false, 'referenceUpdate');
                    }
                    continue; // Don't accumulate reference in main text
                  }

                  // Handle reasoning events
                  if (data.type === 'reasoning') {
                    // Add reasoning step to the collection
                    reasoningSteps.push({
                      content: data.content,
                      timestamp: new Date(data.timestamp * 1000),
                      tool_name: data.tool_name,
                      tool_args: data.tool_args,
                      tool_result: data.tool_result
                    });

                    // Update the AI message with reasoning steps
                    const lastMessageIndex = messages.length - 1;
                    if (lastMessageIndex >= 0 && messages[lastMessageIndex].sender === 'ai') {
                      messages[lastMessageIndex] = {
                        ...messages[lastMessageIndex],
                        reasoningSteps: [...reasoningSteps]
                      };
                      set({ messages }, false, 'reasoningUpdate');
                    }
                    continue; // Don't accumulate reasoning in main text
                  }

                  // Handle AI message content and set thread ID if not set
                  if (!get().currentThreadId && data.thread_id) {
                    set({ currentThreadId: data.thread_id });
                  }

                  // Handle suggested questions if present
                  if (data.type === 'suggested_questions') {
                    suggestedQuestions = data.questions;
                  }

                  if (typeof data.content === 'string') {
                    accumulatedText += data.content;
                  }
                  const lastMessageIndex = messages.length - 1;

                  if (lastMessageIndex >= 0 && messages[lastMessageIndex].sender === 'ai') {
                    messages[lastMessageIndex] = {
                      ...messages[lastMessageIndex],
                      text: accumulatedText,
                      // Update the message ID with the database ID from the backend
                      id: data.message_id || messages[lastMessageIndex].id,
                      // Add suggested questions to the message
                      suggestedQuestions: suggestedQuestions.length > 0 ? suggestedQuestions : undefined,
                      // Preserve reasoning steps
                      reasoningSteps: reasoningSteps.length > 0 ? reasoningSteps : undefined,
                      // Preserve references
                      references: references.length > 0 ? references : undefined
                    };
                    set({ messages }, false, 'streamUpdate');
                  } 
                } catch (e) {
                  console.error('Error parsing JSON:', e);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Chat API error:', error);
        addMessage({
          text: 'Sorry, I encountered an error. Please try again.',
          sender: 'ai'
        });
        setLoading(false);
      }
    },

    submitFeedback: async (feedbackData: FeedbackData) => {
      const { success, error } = useToastStore.getState();
      const { currentThreadId } = get();
      try {
        const response = await ChatAPI.submitFeedback(feedbackData, currentThreadId || 'thread-default');
        success("Success", `Feedback submitted successfully`);
        return response;
      } catch (err) {
        error("Error", `Feedback submission error: ${err}`);
        return err;
      }
    },

    createNewChat: async () => {
      try {
        // Clear current thread and create a blank thread - thread will be created on first message
        set({
          messages: [{
            id: 'welcome-' + Date.now(),
            text: 'Hello! I\'m your AI assistant. How can I help you with your procurement analysis today?',
            sender: 'ai',
            timestamp: new Date()
          }],
          currentThreadId: null,
          inputValue: '' // Clear any existing input
        });
      } catch (error) {
        console.error('Failed to create new chat:', error);
      }
    },

    loadThread: async (threadId: string) => {

      try {
        set({ isLoading: true });
        const messages = await ChatAPI.getMessages(threadId);

        const formattedMessages: Message[] = messages.map(msg => {
          const formattedMsg: Message = {
            id: msg.id,
            text: msg.content,
            sender: msg.role === 'user' ? 'user' : 'ai',
            timestamp: new Date(msg.created_at)
          };

          // Parse and add reasoning_steps if present
          if (msg.reasoning_steps && Array.isArray(msg.reasoning_steps)) {
            formattedMsg.reasoningSteps = msg.reasoning_steps.map(step => ({
              content: step.content,
              timestamp: new Date(step.timestamp),
              tool_name: step.tool_name,
              tool_args: step.tool_args,
              tool_result: step.tool_result
            }));
          }

          // Parse and add references if present
          if (msg.references && Array.isArray(msg.references)) {
            formattedMsg.references = msg.references.map(ref => ({
              reference_id: ref.reference_id,
              reference_type: ref.reference_type,
              title: ref.title,
              file_id: ref.file_id,  // Include file_id for document references
              page_number: ref.page_number,  // Include page_number for document references
              data: ref.data,
              timestamp: new Date(ref.timestamp)
            }));
          }

          return formattedMsg;
        });

        set({
          messages: formattedMessages,
          currentThreadId: threadId,
          isLoading: false
        });
      } catch (error) {
        console.error('Failed to load thread:', error);
        set({ isLoading: false });
      }
    },

    loadThreads: async () => {
      try {
        const threads = await ChatAPI.listThreads();
        set({ threads });
      } catch (error) {
        console.error('Failed to load threads:', error);
      }
    },

    setCurrentThreadId: (threadId: string | null) => {
      set({ currentThreadId: threadId });
    },
    /* ---------- Document previews ---------- */
    loadDocumentPreview: async (reference) => {
      if (!reference || reference.reference_type !== 'document' || !reference.file_id) {
        return;
      }

      const referenceId = reference.reference_id;
      const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // Revoke any existing URL before fetching a fresh one
      const existing = get().documentPreviews[referenceId];
      if (existing?.objectUrl) {
        URL.revokeObjectURL(existing.objectUrl);
      }

      set((state) => ({
        documentPreviews: {
          ...state.documentPreviews,
          [referenceId]: {
            url: null,
            loading: true,
            error: null,
            objectUrl: null,
            requestId
          }
        }
      }), false, 'loadDocumentPreview:start');

      try {
        const blob = await ChatAPI.getDocumentReference(reference.file_id);
        const objectUrl = URL.createObjectURL(blob);
        const finalUrl = reference.page_number
          ? `${objectUrl}#page=${reference.page_number}`
          : objectUrl;

        // Drop late responses if another request superseded this one
        const current = get().documentPreviews[referenceId];
        if (current?.requestId !== requestId) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        set((state) => ({
          documentPreviews: {
            ...state.documentPreviews,
            [referenceId]: {
              url: finalUrl,
              loading: false,
              error: null,
              objectUrl,
              requestId
            }
          }
        }), false, 'loadDocumentPreview:success');
      } catch (err) {
        console.error("Error loading document reference", err);
        set((state) => ({
          documentPreviews: {
            ...state.documentPreviews,
            [referenceId]: {
              url: null,
              loading: false,
              error: err instanceof Error ? err.message : 'Unknown error',
              objectUrl: undefined,
              requestId
            }
          }
        }), false, 'loadDocumentPreview:error');
      }
    },

    clearDocumentPreview: (referenceId) => {
      const previews = { ...get().documentPreviews };
      const preview = previews[referenceId];
      if (preview?.objectUrl) {
        URL.revokeObjectURL(preview.objectUrl);
      }
      delete previews[referenceId];
      set({ documentPreviews: previews }, false, 'clearDocumentPreview');
    },

    /* ---------- Config setters wired to API (🔑) ---------- */

    setApiBase: (base: any) => {
      set({ apiBase: base }, false, 'setApiBase');
      ChatAPI.setBaseUrl(base);
      (ChatAPI as any)?.setBaseUrl?.(base);
    },
  }), { name: 'ChatStore' })

);
/* ---------- Tiny helpers you can import from the web component wrapper ---------- */
export const setApiBase = (base: string) => useChatStore.getState()?.setApiBase(base);
export const setNegotiationId = (negotiationId?: number | string | null) => useChatStore.getState()?.setNegotiationId(negotiationId);
export const setAgentType = (agentType?: string | null) => useChatStore.getState()?.setAgentType(agentType);
export const setSupplierId = (supplierId?: number | string | null) => useChatStore.getState()?.setSupplierId(supplierId);
export const setTenderId = (tenderId?: number | string | null) => useChatStore.getState()?.setTenderId(tenderId);
